import { Link } from 'react-router-dom';
import {
  Mic,
  Languages,
  Heart,
  MessageCircle,
  Share2,
  PlayCircle,
  Image as ImageIcon,
  Video,
  Radio,
  Sparkles,
  ShoppingBag,
  Send,
  Wand2,
  UserPlus,
  Volume2,
  Headphones,
  Globe,
  Smile,
  Camera,
  Layers,
  ShieldCheck,
  Megaphone,
  Clock,
  CalendarClock,
} from 'lucide-react';
import {
  PARTICIPANTS,
  POSTS,
  HERMES_SKILLS,
  COMPOSER_OPTIONS,
  LANGUAGE_PAIRS,
  VOICE_OPTIONS,
  SOCIAL_BADGES,
  FLOW_REEL_DEMO,
  DIFFERENTIATORS,
  findParticipant,
  formatCount,
} from '../../data/socialDemoContent';

// InfoFlowSocialShowcase — section premium de presentation d'InfoFlow Social.
// 100% statique : aucune fetch, aucun axios, aucun appel API, aucun upload.
// Les aperçus restent statiques, mais les CTAs publics pointent vers le
// réseau social actif /social. Les fonctions audio/traduction sont affichées
// comme roadmap, jamais comme boutons morts.

const COMPOSER_ICONS = {
  'photo-video': Camera,
  audio: Mic,
  live: Radio,
  gif: Smile,
  hermes: Wand2,
  translate: Languages,
  voice: Volume2,
  product: ShoppingBag,
  whatsapp: Send,
  telegram: Send,
  invite: UserPlus,
};

const POST_TYPE_ICONS = {
  texte: ImageIcon,
  audio: Headphones,
  business: ShoppingBag,
  video: Video,
  image: ImageIcon,
};

const DIFFERENTIATOR_ICONS = {
  listen: PlayCircle,
  hermes: Wand2,
  multiformat: Layers,
  verified: ShieldCheck,
  mission: Megaphone,
  voice: Volume2,
};

const STATUS_TONE = {
  publié: 'cream',
  'en direct': 'red',
  programmé: 'gold',
  'mis à jour': 'cream',
};

function Initials({ name, color }) {
  const initials = name
    .split(' ')
    .map((part) => part.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <div
      className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center font-bold text-sm"
      style={{
        background: color || 'var(--if-black, #1A1A1A)',
        color: 'var(--if-cream, #FAF7F0)',
      }}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
}

function Badge({ children, tone = 'cream' }) {
  const palette = {
    cream: { bg: 'var(--if-cream, #FAF7F0)', fg: 'var(--if-black, #1A1A1A)', border: 'var(--if-border, rgba(26,26,26,0.08))' },
    gold: { bg: 'var(--if-gold, #E9C46A)', fg: 'var(--if-black, #1A1A1A)', border: 'transparent' },
    red: { bg: 'var(--if-red, #D62828)', fg: 'var(--if-cream, #FAF7F0)', border: 'transparent' },
    black: { bg: 'var(--if-black, #1A1A1A)', fg: 'var(--if-cream, #FAF7F0)', border: 'transparent' },
  }[tone] || { bg: 'transparent', fg: 'var(--if-black, #1A1A1A)', border: 'var(--if-border, rgba(26,26,26,0.08))' };
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-[0.12em]"
      style={{ background: palette.bg, color: palette.fg, border: `1px solid ${palette.border}` }}
    >
      {children}
    </span>
  );
}

function GlassCard({ children, className = '' }) {
  return (
    <div
      className={`rounded-2xl ${className}`}
      style={{
        background: 'var(--if-glass, rgba(250, 247, 240, 0.72))',
        border: '1px solid var(--if-border, rgba(26, 26, 26, 0.08))',
        boxShadow: 'var(--if-shadow-soft, 0 8px 24px rgba(26, 26, 26, 0.06))',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}
    >
      {children}
    </div>
  );
}

function Hero() {
  return (
    <header className="text-center space-y-5 sm:space-y-6 max-w-3xl mx-auto">
      <div className="flex flex-wrap items-center justify-center gap-2">
        <Badge tone="red">
          <Sparkles className="w-3 h-3" /> InfoFlow Social
        </Badge>
        <Badge tone="gold">Version bêta active</Badge>
      </div>
      <h2
        className="font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight"
        style={{ color: 'var(--if-black, #1A1A1A)' }}
      >
        Le réseau social <span style={{ color: 'var(--if-red, #D62828)' }}>éditorial</span> intelligent d’InfoFlow
      </h2>
      <p
        className="text-base sm:text-lg leading-relaxed max-w-2xl mx-auto"
        style={{ color: 'var(--if-black, #1A1A1A)', opacity: 0.74 }}
      >
        Publiez vos prises de parole éditoriales, campagnes et annonces dans un espace bêta contrôlé.
      </p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <Badge tone="cream">Bêta contrôlée</Badge>
        {SOCIAL_BADGES.filter((label) => !label.toLowerCase().includes('disponible')).map((label) => (
          <Badge key={label} tone="cream">{label}</Badge>
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
        <Link
          to="/social"
          className="inline-flex items-center justify-center px-6 py-3 rounded-full text-sm sm:text-base font-semibold transition-transform active:scale-[0.98]"
          style={{
            background: 'var(--if-black, #1A1A1A)',
            color: 'var(--if-cream, #FAF7F0)',
            boxShadow: 'var(--if-shadow-soft, 0 8px 24px rgba(26, 26, 26, 0.06))',
          }}
        >
          Entrer dans le réseau
        </Link>
        <Link
          to="/u/infoflow-demo"
          className="inline-flex items-center justify-center px-6 py-3 rounded-full text-sm sm:text-base font-semibold transition-transform active:scale-[0.98]"
          style={{
            background: 'var(--if-cream, #FAF7F0)',
            color: 'var(--if-black, #1A1A1A)',
            border: '1px solid var(--if-border, rgba(26, 26, 26, 0.08))',
          }}
        >
          Voir la démo
        </Link>
        <Link
          to="/social"
          className="inline-flex items-center justify-center px-6 py-3 rounded-full text-sm sm:text-base font-semibold transition-transform active:scale-[0.98]"
          style={{
            background: 'transparent',
            color: 'var(--if-black, #1A1A1A)',
            border: '1px solid var(--if-border, rgba(26, 26, 26, 0.08))',
          }}
        >
          Publier depuis le réseau
        </Link>
      </div>
    </header>
  );
}

function PostCard({ post }) {
  const author = findParticipant(post.authorId);
  const TypeIcon = POST_TYPE_ICONS[post.type] || ImageIcon;
  const isLive = post.status === 'en direct';
  const isScheduled = post.status === 'programmé';
  const TimeIcon = isScheduled ? CalendarClock : Clock;
  const statusTone = STATUS_TONE[post.status] || 'cream';
  return (
    <GlassCard className="p-5 flex flex-col gap-4 h-full">
      <div className="flex items-start gap-3">
        <Initials name={author.name} color={author.color} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm" style={{ color: 'var(--if-black, #1A1A1A)' }}>
              {author.name}
            </span>
            <span
              className="text-[11px] uppercase tracking-wider"
              style={{ color: 'var(--if-black, #1A1A1A)', opacity: 0.55 }}
            >
              {author.role}
            </span>
          </div>
          <div
            className="text-xs mt-0.5"
            style={{ color: 'var(--if-black, #1A1A1A)', opacity: 0.55 }}
          >
            {author.city} · {author.languages.join(' / ')}
          </div>
          <div className="flex items-center gap-2 flex-wrap mt-1.5">
            <Badge tone={statusTone}>
              {isLive && (
                <span
                  aria-hidden="true"
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: 'var(--if-cream, #FAF7F0)' }}
                />
              )}
              {post.status}
            </Badge>
            <time
              dateTime={post.createdAt}
              className="inline-flex items-center gap-1 text-[11px]"
              style={{ color: 'var(--if-black, #1A1A1A)', opacity: 0.6 }}
            >
              <TimeIcon className="w-3 h-3" aria-hidden="true" />
              {post.displayTime}
            </time>
          </div>
        </div>
        <TypeIcon
          className="w-4 h-4 shrink-0 mt-1"
          style={{ color: 'var(--if-red, #D62828)' }}
          aria-hidden="true"
        />
      </div>
      <p
        className="text-sm sm:text-[15px] leading-relaxed flex-1"
        style={{ color: 'var(--if-black, #1A1A1A)', opacity: 0.86 }}
      >
        {post.text}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {post.badges.map((b) => (
          <Badge key={b} tone={b === 'Hermès' ? 'gold' : b === 'Démo' ? 'cream' : 'cream'}>
            {b}
          </Badge>
        ))}
      </div>
      <div
        className="flex items-center justify-between text-[12px] pt-2"
        style={{ color: 'var(--if-black, #1A1A1A)', opacity: 0.62, borderTop: '1px solid var(--if-border, rgba(26,26,26,0.08))' }}
      >
        <span>{formatCount(post.counters.vues)} vues</span>
        {post.counters.ecoutes > 0 && <span>{formatCount(post.counters.ecoutes)} écoutes</span>}
        <span>{formatCount(post.counters.reactions)} réactions</span>
        <span>{formatCount(post.counters.commentaires)} commentaires</span>
      </div>
      <p className="text-xs font-medium" style={{ color: 'var(--if-black, #1A1A1A)', opacity: 0.58 }}>
        Audio, traduction et réactions avancées sont en roadmap. Le réseau bêta actif se trouve sur /social.
      </p>
    </GlassCard>
  );
}

function DifferentiatorsRow() {
  return (
    <section aria-labelledby="if-social-diff" className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <h3
          id="if-social-diff"
          className="font-serif text-2xl sm:text-3xl"
          style={{ color: 'var(--if-black, #1A1A1A)' }}
        >
          Ce qui rend InfoFlow Social différent
        </h3>
        <span
          className="text-xs uppercase tracking-[0.14em]"
          style={{ color: 'var(--if-black, #1A1A1A)', opacity: 0.55 }}
        >
          2026
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
        {DIFFERENTIATORS.map((d) => {
          const Icon = DIFFERENTIATOR_ICONS[d.iconKey] || Sparkles;
          return (
            <GlassCard key={d.id} className="p-4 flex flex-col gap-2 items-start">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center"
                style={{
                  background: 'var(--if-black, #1A1A1A)',
                  color: 'var(--if-cream, #FAF7F0)',
                }}
                aria-hidden="true"
              >
                <Icon className="w-4 h-4" />
              </div>
              <div
                className="text-sm font-semibold leading-snug"
                style={{ color: 'var(--if-black, #1A1A1A)' }}
              >
                {d.label}
              </div>
            </GlassCard>
          );
        })}
      </div>
    </section>
  );
}

function PostsGrid() {
  return (
    <section aria-labelledby="if-social-posts" className="space-y-5">
      <div className="flex items-end justify-between gap-4">
        <h3
          id="if-social-posts"
          className="font-serif text-2xl sm:text-3xl"
          style={{ color: 'var(--if-black, #1A1A1A)' }}
        >
          Le fil éditorial vivant
        </h3>
        <span
          className="text-xs uppercase tracking-[0.14em]"
          style={{ color: 'var(--if-black, #1A1A1A)', opacity: 0.55 }}
        >
          Démo
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
        {POSTS.map((post) => (
          <PostCard key={post.id} post={post} />
        ))}
      </div>
    </section>
  );
}

function ParticipantsRow() {
  return (
    <section aria-labelledby="if-social-participants" className="space-y-5">
      <div className="flex items-end justify-between gap-4">
        <h3
          id="if-social-participants"
          className="font-serif text-2xl sm:text-3xl"
          style={{ color: 'var(--if-black, #1A1A1A)' }}
        >
          Une communauté éditoriale, internationale.
        </h3>
        <span
          className="text-xs uppercase tracking-[0.14em]"
          style={{ color: 'var(--if-black, #1A1A1A)', opacity: 0.55 }}
        >
          Participants démo
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-5 gap-3 sm:gap-4">
        {PARTICIPANTS.map((p) => (
          <GlassCard key={p.id} className="p-4 flex flex-col items-center text-center gap-2">
            <Initials name={p.name} color={p.color} />
            <div className="min-w-0 w-full">
              <div className="font-semibold text-sm truncate" style={{ color: 'var(--if-black, #1A1A1A)' }}>
                {p.name}
              </div>
              <div className="text-[11px] truncate" style={{ color: 'var(--if-black, #1A1A1A)', opacity: 0.6 }}>
                {p.city}
              </div>
              <div className="text-[10px] uppercase tracking-wider mt-0.5 truncate" style={{ color: 'var(--if-black, #1A1A1A)', opacity: 0.5 }}>
                {p.role}
              </div>
              <div className="text-[10px] mt-1 truncate" style={{ color: 'var(--if-black, #1A1A1A)', opacity: 0.6 }}>
                {p.languages.join(' / ')}
              </div>
            </div>
            <Badge tone={p.status === 'Journaliste' ? 'red' : p.status === 'Créateur' ? 'gold' : 'cream'}>
              {p.status}
            </Badge>
          </GlassCard>
        ))}
      </div>
    </section>
  );
}

function ComposerPreview() {
  return (
    <GlassCard className="p-5 sm:p-6 space-y-4">
      <div className="flex items-center gap-3">
        <Initials name="Vous" color="var(--if-red, #D62828)" />
        <div
          className="flex-1 px-4 py-3 rounded-full text-sm sm:text-base"
          style={{
            background: 'var(--if-cream, #FAF7F0)',
            color: 'var(--if-black, #1A1A1A)',
            opacity: 0.65,
            border: '1px solid var(--if-border, rgba(26,26,26,0.08))',
          }}
        >
          Que voulez-vous partager avec le monde ?
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {COMPOSER_OPTIONS.map((opt) => {
          const Icon = COMPOSER_ICONS[opt.id] || Sparkles;
          return (
            <span
              key={opt.id}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold"
              style={{
                background: 'transparent',
                color: 'var(--if-black, #1A1A1A)',
                border: '1px solid var(--if-border, rgba(26,26,26,0.08))',
                opacity: 0.85,
              }}
            >
              <Icon className="w-3.5 h-3.5" />
              {opt.label}
            </span>
          );
        })}
      </div>
      <div
        className="text-[11px] uppercase tracking-[0.14em] pt-1"
        style={{ color: 'var(--if-black, #1A1A1A)', opacity: 0.5 }}
      >
        Composer réel disponible sur /social pour les rôles admin et editor.
      </div>
    </GlassCard>
  );
}

function HermesPanel() {
  return (
    <div className="space-y-4">
      <GlassCard className="p-5 space-y-3">
        <Badge tone="gold">
          <Sparkles className="w-3 h-3" /> Hermès roadmap
        </Badge>
        <p className="text-sm leading-relaxed" style={{ color: 'var(--if-black, #1A1A1A)', opacity: 0.72 }}>
          Assistance multi-source, variantes par réseau et recommandations visuelles seront ajoutées après la bêta du feed éditorial.
        </p>
      </GlassCard>
      <GlassCard className="p-5 space-y-3">
        <div
          className="text-[11px] uppercase tracking-[0.14em]"
          style={{ color: 'var(--if-black, #1A1A1A)', opacity: 0.6 }}
        >
          Compétences d’Hermès
        </div>
        <div className="flex flex-wrap gap-1.5">
          {HERMES_SKILLS.map((s) => (
            <Badge key={s.id} tone="cream">{s.label}</Badge>
          ))}
        </div>
      </GlassCard>
    </div>
  );
}

function VoiceLanguagesBlock() {
  return (
    <GlassCard className="p-5 sm:p-6 space-y-5">
      <div className="space-y-1.5">
        <Badge tone="black">
          <Globe className="w-3 h-3" /> Voix & Langues
        </Badge>
        <h3
          className="font-serif text-2xl sm:text-3xl leading-tight"
          style={{ color: 'var(--if-black, #1A1A1A)' }}
        >
          Parlez à n’importe qui, dans n’importe quelle langue.
        </h3>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        {LANGUAGE_PAIRS.map((pair) => (
          <div
            key={pair.id}
            className="px-3 py-2.5 rounded-xl text-center"
            style={{
              background: 'var(--if-cream, #FAF7F0)',
              border: '1px solid var(--if-border, rgba(26,26,26,0.08))',
            }}
          >
            <div className="text-xs font-semibold" style={{ color: 'var(--if-black, #1A1A1A)' }}>
              {pair.from}
            </div>
            <div className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--if-red, #D62828)' }}>
              ↓
            </div>
            <div className="text-xs font-semibold" style={{ color: 'var(--if-black, #1A1A1A)' }}>
              {pair.to}
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        {VOICE_OPTIONS.map((v) => (
          <Badge key={v.id} tone={v.id === 'cloned' ? 'gold' : 'cream'}>{v.label}</Badge>
        ))}
      </div>
      <p className="rounded-xl px-3 py-2 text-xs font-semibold" style={{ background: 'var(--if-cream, #FAF7F0)', color: 'var(--if-black, #1A1A1A)' }}>
        Audio social, traduction et voix premium sont en roadmap.
      </p>
    </GlassCard>
  );
}

function FlowReelsPreview() {
  const reel = FLOW_REEL_DEMO;
  const author = findParticipant(reel.authorId);
  return (
    <div className="space-y-3">
      <div
        className="relative w-full max-w-[280px] mx-auto rounded-2xl overflow-hidden"
        style={{
          aspectRatio: '9 / 16',
          background:
            'linear-gradient(160deg, #1A1A1A 0%, #2A1010 50%, #D62828 100%)',
          boxShadow: 'var(--if-shadow-soft, 0 8px 24px rgba(26, 26, 26, 0.06))',
        }}
      >
        <div className="absolute top-3 left-3">
          <Badge tone="gold">{reel.badge}</Badge>
        </div>
        <div className="absolute bottom-3 left-3 right-3 flex flex-col gap-2 text-left">
          <div className="flex items-center gap-2">
            <Initials name={author.name} color="var(--if-cream, #FAF7F0)" />
            <div className="min-w-0">
              <div className="font-semibold text-sm truncate" style={{ color: 'var(--if-cream, #FAF7F0)' }}>
                {author.name}
              </div>
              <div className="text-[11px] truncate" style={{ color: 'var(--if-cream, #FAF7F0)', opacity: 0.7 }}>
                {reel.language}
              </div>
            </div>
          </div>
          <p
            className="text-sm leading-snug"
            style={{ color: 'var(--if-cream, #FAF7F0)' }}
          >
            {reel.text}
          </p>
          <span
            className="mt-1 inline-flex items-center justify-center rounded-full px-3 py-1.5 text-xs font-semibold"
            style={{ background: 'var(--if-cream, #FAF7F0)', color: 'var(--if-black, #1A1A1A)' }}
          >
            Audio multilingue en roadmap
          </span>
        </div>
        <div className="absolute right-3 bottom-3 flex flex-col items-center gap-3" style={{ color: 'var(--if-cream, #FAF7F0)' }}>
          <div className="flex flex-col items-center text-[11px] font-semibold">
            <Heart className="w-5 h-5" aria-hidden="true" />
            {formatCount(reel.counters.likes)}
          </div>
          <div className="flex flex-col items-center text-[11px] font-semibold">
            <MessageCircle className="w-5 h-5" aria-hidden="true" />
            {formatCount(reel.counters.comments)}
          </div>
          <div className="flex flex-col items-center text-[11px] font-semibold">
            <Share2 className="w-5 h-5" aria-hidden="true" />
            {formatCount(reel.counters.shares)}
          </div>
        </div>
      </div>
      <div
        className="text-center text-[11px] uppercase tracking-[0.14em]"
        style={{ color: 'var(--if-black, #1A1A1A)', opacity: 0.55 }}
      >
        Flow Reels — preview démo
      </div>
    </div>
  );
}

export default function InfoFlowSocialShowcase() {
  return (
    <section
      aria-labelledby="if-social-showcase-title"
      className="py-16 sm:py-20"
      style={{
        background:
          'linear-gradient(180deg, var(--if-cream, #FAF7F0) 0%, rgba(250,247,240,0.4) 100%)',
        borderTop: '1px solid var(--if-border, rgba(26,26,26,0.08))',
        borderBottom: '1px solid var(--if-border, rgba(26,26,26,0.08))',
      }}
    >
      <div id="if-social-showcase-title" className="container mx-auto px-4 space-y-12 sm:space-y-16">
        <Hero />
        <DifferentiatorsRow />
        <PostsGrid />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 sm:gap-6">
          <div className="lg:col-span-2 space-y-5 sm:space-y-6">
            <ComposerPreview />
            <VoiceLanguagesBlock />
          </div>
          <div className="space-y-5 sm:space-y-6">
            <HermesPanel />
            <FlowReelsPreview />
          </div>
        </div>

        <ParticipantsRow />

        {/* Lien compact vers le registre Hermes Skills (page /social).
            Strictement visuel : un Link react-router, aucun fetch, aucun appel API. */}
        <div className="text-center">
          <Link
            to="/social"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-transform active:scale-[0.98]"
            style={{
              background: 'var(--if-cream, #FAF7F0)',
              color: 'var(--if-black, #1A1A1A)',
              border: '1px solid var(--if-border, rgba(26,26,26,0.08))',
              boxShadow: 'var(--if-shadow-soft, 0 8px 24px rgba(26, 26, 26, 0.06))',
            }}
          >
            <Sparkles className="w-4 h-4" aria-hidden="true" />
            Decouvrir le Hermes Skills Registry
          </Link>
        </div>
      </div>
    </section>
  );
}
